import moment from 'moment';
import { createSelector } from 'reselect';
import { AsyncJob, AsyncJobStatus, AsyncJobType } from 'types';
import { RootState } from '../reducer';

export function selectNotifications(state: RootState) {
  return state.notifications.notifications;
}

export const selectGroupedNotifications = createSelector(
  selectNotifications,
  notifications => {
    const delayedMessages = [
      AsyncJobType.RECALCULATE_COMPOSITE_TAG,
      AsyncJobType.ADD_INDEXED_DATA_CUTS,
      AsyncJobType.REMOVE_INDEXED_DATA_CUTS,
      AsyncJobType.RECALCULATE_DATA_MODEL_COMPOSITE_STATS,
      AsyncJobType.RECALCULATE_DATA_MODEL_WITH_AUTO_SPREAD,
    ];

    return Object.entries(
      notifications.filter((notification) => {
        if (notification.type === AsyncJobType.CLEANUP_CS_ADMIN_TENANCIES) {
          return false;
        }
        // filter messages that usually processed in couple seconds, to not spam users
        // almost always such messages will be removed before they become visible on UI
        if (delayedMessages.includes(notification.type)) {
          return moment().diff(moment(notification.createdAt), 'seconds') > 60;
        }
        return true;
      }).reduce(
        (result: Record<AsyncJobType, AsyncJob[]>, notification) => {
          const notificationGroup = result[notification.type] || [];
          return {
            ...result,
            [notification.type]: [...notificationGroup, notification],
          };
        },
        {} as Record<AsyncJobType, AsyncJob[]>,
      )
    ).reduce((result: { type: AsyncJobType, notifications: AsyncJob[], inProgress: boolean }[], [type, notifications]) => [
      ...result,
      {
        type: type as AsyncJobType,
        notifications,
        inProgress: !!notifications.find(notification => notification.status === AsyncJobStatus.IN_PROGRESS),
      },
    ], []);
  }
);

export const selectRecalculateCompositeNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type === AsyncJobType.RECALCULATE_COMPOSITES || notification.type === AsyncJobType.RECALCULATE_AGING_FACTOR
  )
)

export const selectPlanNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type === AsyncJobType.MIGRATE_PLAN || notification.type === AsyncJobType.UPDATE_PLAN
  )
)

export const selectExportNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type === AsyncJobType.EXPORT_DATA
  )
)

export const selectRecalculateCompositesNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type === AsyncJobType.RECALCULATE_COMPOSITES
  )
)

export const selectRecalculateDataModelCompositeStatsNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type === AsyncJobType.RECALCULATE_DATA_MODEL_COMPOSITE_STATS
  )
)

export const selectRecalculateDataModelWithAutoSpreadNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type === AsyncJobType.RECALCULATE_DATA_MODEL_WITH_AUTO_SPREAD
  )
)

export const selectPublishSurveyReplacementNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type === AsyncJobType.PUBLISH_SURVEY_REPLACEMENT,
  )
)

export const selectApplyPolicySurveyReplacementNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type === AsyncJobType.APPLY_CUT_SELECTION_POLICY_SURVEY_REPLACEMENT,
  )
)

export const selectActualizeCompositeDataCutsSurveyReplacementNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type === AsyncJobType.ACTUALIZE_COMPOSITE_DATA_CUT_REPLACEMENT,
  )
)

export const selectActualizeJobMappingsSurveyReplacementNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type === AsyncJobType.ACTUALIZE_SURVEY_JOB_MAPPING_REPLACEMENT,
  )
)

export const selectHrisTestNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type === AsyncJobType.TEST_HRIS_URL,
  )
)

export const selectPolicyManagerNotifications = createSelector(
  selectNotifications,
  (notifications) => notifications.filter((notification) =>
    notification.type ===  AsyncJobType.APPLY_WEIGHTING_POLICY || notification.type === AsyncJobType.RESET_DEFAULT_CUTS
  )
)
